import { apiClient } from '../../shared/apiClient';
import {
  SystemSetting,
  AdminAction,
  SystemHealthMetric,
  BillingSubscription,
  UsageAnalytics,
  FeatureFlag,
  SystemNotification,
} from '../../../hooks/entities/admin/useAdminConsole';

class AdminService {
  // System Settings
  async getSystemSettings(tenantId?: string, category?: string): Promise<SystemSetting[]> {
    return apiClient.get<SystemSetting[]>('/admin/system-settings', {
      tenantId,
      category,
    });
  }

  async updateSystemSetting(
    settingId: string,
    value: string,
    userId: string,
  ): Promise<SystemSetting> {
    return apiClient.put<SystemSetting>(`/admin/system-settings/${settingId}`, {
      settingValue: value,
      lastModifiedBy: userId,
    });
  }

  // Admin Actions
  async getAdminActions(
    tenantId?: string,
    filters?: {
      actionType?: string;
      actionCategory?: string;
      performedBy?: string;
      severity?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<AdminAction[]> {
    return apiClient.get<AdminAction[]>('/admin/actions', {
      tenantId,
      ...filters,
    });
  }

  async logAdminAction(
    tenantId: string | undefined,
    userId: string,
    actionData: Omit<
      AdminAction,
      'id' | 'tenantId' | 'performedBy' | 'performedByName' | 'performedByRole' | 'createdAt'
    >,
  ): Promise<AdminAction> {
    return apiClient.post<AdminAction>('/admin/actions', {
      tenantId,
      performedBy: userId,
      ...actionData,
    });
  }

  // Health Metrics
  async getHealthMetrics(
    tenantId?: string,
    category?: string,
    timeRange?: string,
  ): Promise<SystemHealthMetric[]> {
    return apiClient.get<SystemHealthMetric[]>('/admin/health-metrics', {
      tenantId,
      category,
      timeRange,
    });
  }

  // System Overview
  async getSystemOverview(tenantId?: string): Promise<{
    totalUsers: number;
    activeUsers: number;
    totalTenants: number;
    activeTenants: number;
    systemHealth: string;
    uptimePercentage: number;
    responseTime: number;
    errorRate: number;
    lastUpdated: string;
  }> {
    return apiClient.get('/admin/system-overview', { tenantId });
  }

  // Billing
  async getBillingSubscriptions(tenantId?: string): Promise<BillingSubscription[]> {
    return apiClient.get<BillingSubscription[]>('/admin/billing/subscriptions', {
      tenantId,
    });
  }

  async updateBillingSubscription(
    subscriptionId: string,
    updates: Partial<BillingSubscription>,
  ): Promise<BillingSubscription> {
    return apiClient.put<BillingSubscription>(
      `/admin/billing/subscriptions/${subscriptionId}`,
      updates,
    );
  }

  // Usage Analytics
  async getUsageAnalytics(
    tenantId?: string,
    period?: 'daily' | 'weekly' | 'monthly',
    startDate?: string,
    endDate?: string,
  ): Promise<UsageAnalytics[]> {
    return apiClient.get<UsageAnalytics[]>('/admin/analytics/usage', {
      tenantId,
      period,
      startDate,
      endDate,
    });
  }

  async getAnalyticsSummary(
    tenantId?: string,
    period?: string,
  ): Promise<{
    totalUsers: number;
    newUsers: number;
    activeUsers: number;
    userRetentionRate: number;
    totalTransactions: number;
    totalRevenue: number;
    averageSessionDuration: number;
    mostUsedFeatures: Array<{ feature: string; usage: number }>;
    systemHealth: string;
  }> {
    return apiClient.get('/admin/analytics/summary', {
      tenantId,
      period,
    });
  }

  // Feature Flags
  async getFeatureFlags(tenantId?: string): Promise<FeatureFlag[]> {
    return apiClient.get<FeatureFlag[]>('/admin/feature-flags', {
      tenantId,
    });
  }

  async updateFeatureFlag(flagId: string, updates: Partial<FeatureFlag>): Promise<FeatureFlag> {
    return apiClient.put<FeatureFlag>(`/admin/feature-flags/${flagId}`, updates);
  }

  // System Notifications
  async getSystemNotifications(
    tenantId?: string,
    userId?: string,
    filters?: {
      notificationType?: string;
      severity?: string;
      status?: string;
    },
  ): Promise<SystemNotification[]> {
    return apiClient.get<SystemNotification[]>('/admin/notifications', {
      tenantId,
      userId,
      ...filters,
    });
  }

  async createSystemNotification(
    tenantId: string | undefined,
    userId: string,
    notificationData: Omit<
      SystemNotification,
      | 'id'
      | 'tenantId'
      | 'status'
      | 'viewCount'
      | 'acknowledgmentCount'
      | 'escalated'
      | 'escalatedTo'
      | 'createdAt'
    >,
  ): Promise<SystemNotification> {
    return apiClient.post<SystemNotification>('/admin/notifications', {
      tenantId,
      createdBy: userId,
      ...notificationData,
    });
  }

  async acknowledgeSystemNotification(notificationId: string, userId: string): Promise<void> {
    await apiClient.put(`/admin/notifications/${notificationId}/acknowledge`, {
      acknowledgedBy: userId,
    });
  }

  async dismissSystemNotification(notificationId: string, userId: string): Promise<void> {
    await apiClient.put(`/admin/notifications/${notificationId}/dismiss`, {
      dismissedBy: userId,
    });
  }
}

export const adminService = new AdminService();
